import { z } from "zod";

import type {
    TSession,
    TSplitRecording,
    TSplitRecordingSegment,
} from "~/database/types";

// AIDEV-NOTE: Shared contract for the recording splitter (/sessions/new/split).
// Imported by both client and server — keep server-only imports out.

export const RECORDING_SPLIT_FLAG_KEY = "enable-recording-split";
export const RECORDING_SPLIT_USERS_SEGMENT_NAME = "Recording Split Users";
export const RECORDING_SPLIT_PRACTITIONERS_SEGMENT_NAME =
    "Recording Split Practitioners";

// 1-3 slice points -> 2-4 segments.
export const MIN_SEGMENT_COUNT = 2;
export const MAX_SEGMENT_COUNT = 4;
export const MAX_SLICE_POINTS = MAX_SEGMENT_COUNT - 1;
// Segments shorter than this are almost certainly a mis-drag, and the
// pipeline rejects transcripts under 200 words anyway.
export const MIN_SEGMENT_SEC = 60;

// AIDEV-NOTE: Fixed 4-slot palette from house tokens (tailwind.config.ts brand
// scales). Index-aligned with segments everywhere: waveform region tints,
// label-card accents, progress cards, completion cards.
export interface SegmentColor {
    /** Canvas/inline-style hex. */
    hex: string;
    /** Tailwind text class for chips/labels. */
    textClass: string;
    /** Tailwind background class for solid chips. */
    bgClass: string;
}

export const SEGMENT_COLORS: readonly SegmentColor[] = [
    {
        hex: "#5A9578",
        textClass: "text-brand-forest-500",
        bgClass: "bg-brand-forest-500",
    }, // forest-500
    {
        hex: "#E5653B",
        textClass: "text-brand-terra-500",
        bgClass: "bg-brand-terra-500",
    }, // terra-500
    {
        hex: "#8B6FA6",
        textClass: "text-brand-plum-500",
        bgClass: "bg-brand-plum-500",
    }, // plum-500
    {
        hex: "#1B4332",
        textClass: "text-brand-forest-800",
        bgClass: "bg-brand-forest-800",
    }, // forest-800
];

const trimmedNonEmpty = (label: string) =>
    z.string().trim().min(1, `${label} is required`);

export const splitPersonSchema = z.object({
    firstName: trimmedNonEmpty("First name"),
    lastName: trimmedNonEmpty("Last name"),
    email: z.string().trim().toLowerCase().email("Enter a full email address"),
});

export const dropRangeSchema = z.object({
    startMs: z.number().int().min(0),
    endMs: z.number().int().positive(),
});

export const splitSegmentInputSchema = z.object({
    startMs: z.number().int().min(0),
    endMs: z.number().int().positive(),
    // AIDEV-NOTE: In-session stretches to cut from the audio before the
    // pipeline runs (facilitator pop-ins, consults). Absolute recording
    // times, must fall inside [startMs, endMs].
    dropRanges: z.array(dropRangeSchema).max(10).default([]),
    client: splitPersonSchema,
    practitioner: splitPersonSchema,
});

// AIDEV-NOTE: The uploaded Zoom transcript always lands at this name inside
// the recording's storage folder, so no schema column is needed for it.
export const SPLIT_TRANSCRIPT_FILENAME = "transcript.vtt";

export const commitRequestSchema = z
    .object({
        recordingId: z.string().uuid(),
        /** Storage path of the uploaded media master; absent = transcript-only. */
        masterPath: z.string().min(1).optional(),
        /** True when the Zoom .vtt was uploaded to the recording folder. */
        hasTranscript: z.boolean().default(false),
        recordedAtIso: z.string().datetime({ offset: true }).optional(),
        segments: z
            .array(splitSegmentInputSchema)
            .min(MIN_SEGMENT_COUNT)
            .max(MAX_SEGMENT_COUNT),
    })
    .refine((value) => value.masterPath || value.hasTranscript, {
        message: "A recording or a transcript is required",
    });

export const beginUploadRequestSchema = z.object({
    originalFilename: z.string().trim().min(1).max(512),
    durationSeconds: z.number().int().positive().optional(),
    recordedAtIso: z.string().datetime({ offset: true }).optional(),
});

export type SplitPersonInput = z.infer<typeof splitPersonSchema>;
export type SplitSegmentInput = z.infer<typeof splitSegmentInputSchema>;
export type CommitRequest = z.infer<typeof commitRequestSchema>;
export type BeginUploadRequest = z.infer<typeof beginUploadRequestSchema>;

export interface BeginUploadResponse {
    recordingId: string;
    upload: {
        bucket: string;
        folder: string;
    };
}

export interface CommitResponseSegment {
    segmentId: string;
    segmentIndex: number;
    sessionId: string;
}

export interface CommitResponse {
    recordingId: string;
    segments: CommitResponseSegment[];
}

export type SplitRecordingStatus = TSplitRecording["status"];
export type SplitSegmentSliceStatus = TSplitRecordingSegment["sliceStatus"];

export interface SplitStatusSegment {
    segmentId: string;
    segmentIndex: number;
    startMs: number;
    endMs: number;
    sliceStatus: SplitSegmentSliceStatus;
    sliceError: string | null;
    sessionId: string | null;
    sessionStatus: TSession["status"] | null;
    clientFirstName: string;
    clientLastName: string;
    clientEmail: string;
    practitionerFirstName: string;
    practitionerLastName: string;
    practitionerEmail: string;
}

export interface SplitStatusResponse {
    recording: {
        id: string;
        status: SplitRecordingStatus;
        originalFilename: string | null;
        durationSeconds: number | null;
        recordedAt: string;
    };
    segments: SplitStatusSegment[];
}

export interface PractitionerOption {
    email: string;
    fullName: string | null;
}

// ---- VTT auto-slicing analysis ----

export const analyzeRequestSchema = z.object({
    recordingId: z.string().uuid(),
    /** Raw WebVTT text from the Zoom cloud-recording transcript. */
    vtt: z.string().min(10).max(3_000_000),
});
export type AnalyzeRequest = z.infer<typeof analyzeRequestSchema>;

export interface TimeRangeSec {
    startSec: number;
    endSec: number;
}

export interface ProposedSession {
    startSec: number;
    endSec: number;
    /** First name as spoken in the transcript, e.g. "Sky". */
    practitionerName: string;
    clientName: string;
    /** Facilitator pop-ins / consults to cut, inside [startSec, endSec]. */
    interruptions: TimeRangeSec[];
}

export interface AnalyzeResponse {
    sessions: ProposedSession[];
    /** Distinct speaker names seen in the VTT (facilitator included). */
    speakerNames: string[];
}
